import { makeAutoObservable } from "mobx";
import {
  BeneficialOwnersSearchResponse,
  downloadFile,
  getResponseFilename,
  HttpStatus,
  isHttpError,
} from "../../api";
import { logError } from "../../utils/log";
import { failure, isFailure, isOk, isPending, ok, pending, Result } from "../../common/common";
import { TMessageKey } from "../../i18n/types.gen";
import {
  LocalDateFieldModel,
  minLengthValidator,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "../../common/commonForms";
import AlertMessageStore from "../../stores/AlertMessageStore";
import { norwegianPersonalIdValidator } from "../../utils/other";
import { newUuid } from "../../utils/uuid";
import { Language } from "../../i18n/languages";
import { getAllCountriesSortedByName } from "../../utils/countries";
import { SuggestedCompany } from "../../types/CompanyInfo";
import { TMessage } from "../../i18n/tt";
import { compact, countBy, identity } from "lodash";
import { DCEvent, navigate } from "../../events/eventsDomain";
import { adminBeneficialOwners, adminNewBeneficialOwnersStatement } from "../../utils/navigation";
import UserStore from "../../stores/UserStore";
import { DownloadService } from "../../api/DownloadService";
import { AxiosInstance } from "axios";

export function isMissingAddress(c: IntermediateCompany) {
  return c.foreignCompany && c.foreignCompany.addressLine1.length < 3;
}

function isCompanyMissingAddress(intermediateCompanies?: IntermediateCompany[]) {
  return intermediateCompanies?.find((c) => isMissingAddress(c));
}

function findCompanyMissingAddress(owner: BeneficialOwner) {
  switch (owner.positionType) {
    case "OWNERSHIP":
      return isCompanyMissingAddress(owner.ownershipPosition?.intermediateCompanies);
    case "VOTING_RIGHTS":
      return isCompanyMissingAddress(owner.ownershipPosition?.intermediateCompanies);
  }
}

export class BeneficialOwnersStore {
  fetchResult?: Result<BeneficialOwnersResponse | null>;
  sessionResult?: Result<BeneficialOwnersState | null>;
  submitResult?: Result;
  showAddDialogForModel?: BeneficialOwnerModel;
  showEditDialogForModel?: BeneficialOwnerModel;
  showDeleteDialogForBeneficialOwner?: BeneficialOwner;
  model?: BeneficialOwnersModel;
  showSearchDialog = false;
  searchResult?: Result<OptionFieldModel<BeneficialOwner>>;
  mapResult?: Result<string>;
  ownersMapResult?: Result<string>;
  showFullScreen: boolean = false;
  showMap: "beneficial-owners" | "owners" = "beneficial-owners";

  constructor(
    readonly apiService: BeneficialOwnersApiClient,
    readonly downloadService: DownloadService,
    readonly messageStore: AlertMessageStore,
    readonly userStore: UserStore,
    readonly dispatch: (events: DCEvent[]) => void,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  *fetch() {
    this.fetchResult = pending();
    try {
      const response: BeneficialOwnersResponse | null = yield this.apiService.fetchBeneficialOwners(
        this.userStore.orgNumber,
      );
      this.fetchResult = ok(response);
    } catch (e) {
      this.fetchResult = failure();
    }
  }

  *fetchStatement(statementId: string) {
    this.fetchResult = pending();
    try {
      const response: BeneficialOwnersResponse | null =
        yield this.apiService.fetchBeneficialOwnersStatement(this.userStore.orgNumber, statementId);
      this.fetchResult = ok(response);
    } catch (e) {
      this.fetchResult = failure();
    }
  }

  *fetchBeneficialOwnersMap() {
    this.mapResult = pending();
    try {
      const svg: string = yield this.apiService.fetchBeneficialOwnersMap(this.userStore.orgNumber);
      this.mapResult = ok(svg);
    } catch (e) {
      this.mapResult = failure();
    }
  }

  *fetchOwnersMap() {
    this.ownersMapResult = pending();
    try {
      const svg: string = yield this.downloadService.downloadTrankMap(this.userStore.orgNumber, 1);
      this.ownersMapResult = ok(svg);
    } catch (e) {
      this.ownersMapResult = failure();
    }
  }

  *submit(language: Language) {
    if (!this.model) {
      return;
    }
    if (this.model.beneficialOwnersStateType.value === "knownBeneficialOwners") {
      const ownerMissingPosition = this.model.beneficialOwners.find(
        (owner) =>
          (owner.positionType === "OWNERSHIP" && !owner.ownershipPosition) ||
          (owner.positionType === "VOTING_RIGHTS" && !owner.votesPosition),
      );
      if (ownerMissingPosition) {
        this.openEditDialog(ownerMissingPosition, language);
        return;
      }

      const ownerMissingAddress = this.model.beneficialOwners.find((owner) =>
        findCompanyMissingAddress(owner),
      );
      if (ownerMissingAddress) {
        const company = findCompanyMissingAddress(ownerMissingAddress);
        if (company) {
          this.openEditDialog(ownerMissingAddress, language);
          this.showEditDialogForModel?.editIntermediateCompany(language, company);
        }
        return;
      }
    }

    this.model.validate();
    if (!this.model.isValid) {
      return;
    }

    this.model.isDirty = false;
    this.submitResult = pending();
    try {
      yield this.apiService.submitBeneficialOwners(
        this.userStore.orgNumber,
        this.model.prepareData(),
      );
      this.submitResult = ok();

      this.messageStore.showSuccess({ key: "beneficial-owners.success" });
      this.clearModel();
      this.navigateToOverview(true);
    } catch (e) {
      this.submitResult = failure();
      this.handleSubmitError(e, true);
    }
  }

  clearModel() {
    this.model = undefined;
  }

  private handleSubmitError(e: unknown, reauthenticate: boolean) {
    if (isHttpError(HttpStatus.BAD_REQUEST, e) && e.response?.data) {
      const response = e.response.data as SubmitBeneficialOwnersResponse;
      switch (response.type) {
        case "AuthenticationRequired":
          if (reauthenticate) {
            window.location.href = makeAuthenticationUrl(
              response.authUrl,
              this.userStore.orgNumber,
            );
          } else {
            this.messageStore.showError({ key: "beneficial-owners.authentication-failed" });
          }
          break;
        case "ValidationErrors":
          this.messageStore.showError({
            key: "beneficial-owners.validation-errors",
            params: { errors: response.errors },
          });
          break;
        case "NoAccessError":
          this.messageStore.showError({ key: "beneficial-owners.party-not-found" });
          break;
        case "NotAuthorizedError":
          this.messageStore.showError({ key: "beneficial-owners.user-not-authorized" });
          break;
        default:
          this.messageStore.showError({ key: "beneficial-owners.failure" });
          logError("Unknown beneficial owners validation error", response);
      }
    } else {
      this.messageStore.showError({ key: "beneficial-owners.failure" });
    }
  }

  *submitOnReturn() {
    this.submitResult = pending();
    try {
      yield this.apiService.submitBeneficialOwners(this.userStore.orgNumber);
      this.submitResult = ok();

      this.messageStore.showSuccess({ key: "beneficial-owners.success" });
      this.dispatch([navigate(adminBeneficialOwners(this.userStore.orgNumber), { replace: true })]);
    } catch (e) {
      this.submitResult = failure();
      this.handleSubmitError(e, false);
      this.dispatch([
        navigate(adminNewBeneficialOwnersStatement(this.userStore.orgNumber), { replace: true }),
      ]);
    }
  }

  *initModel() {
    if (isOk(this.searchResult)) {
      if (this.searchResult.data.options.length === 0) {
        this.model = new BeneficialOwnersModel();
      } else {
        this.model = new BeneficialOwnersModel({
          type: "knownBeneficialOwners",
          beneficialOwners: this.searchResult.data.selected,
          hasOtherUnknownBeneficialOwners: false,
        });
      }
      this.searchResult = undefined;
    } else if (isFailure(this.submitResult)) {
      // Reload form state from session
      this.sessionResult = pending();
      try {
        const state: BeneficialOwnersState | null =
          yield this.apiService.getBeneficialOwnerSessionState(this.userStore.orgNumber);

        if (state) {
          this.sessionResult = ok(state);
          this.model = new BeneficialOwnersModel(state);
        }
      } catch (e) {
        this.fetchResult = failure();
      }
    } else {
      this.fetchResult = pending();
      try {
        const response: BeneficialOwnersResponse | null =
          yield this.apiService.fetchBeneficialOwners(this.userStore.orgNumber);
        this.fetchResult = ok(response);
        this.model = new BeneficialOwnersModel(response?.beneficialOwnersState);
      } catch (e) {
        this.fetchResult = failure();
      }
    }
  }

  closeAddDialog() {
    this.showAddDialogForModel = undefined;
  }

  openDeleteDialog(beneficialOwner: BeneficialOwner) {
    this.showDeleteDialogForBeneficialOwner = beneficialOwner;
  }

  closeDeleteDialog() {
    this.showDeleteDialogForBeneficialOwner = undefined;
  }

  deleteBeneficialOwner(beneficialOwnerId: string) {
    if (this.model) {
      this.model.beneficialOwners = this.model.beneficialOwners.filter(
        (owner) => owner.id !== beneficialOwnerId,
      );
      this.closeDeleteDialog();
    }
  }

  openAddDialog(language: Language) {
    this.showAddDialogForModel = new BeneficialOwnerModel(
      language,
      this.model?.hasControlBoardMembers() ?? false,
    );
  }

  openEditDialog(beneficialOwner: BeneficialOwner, language: Language) {
    this.showEditDialogForModel = new BeneficialOwnerModel(
      language,
      this.model?.hasControlBoardMembers(beneficialOwner) ?? false,
      beneficialOwner,
    );
  }

  closeEditDialog() {
    this.showEditDialogForModel = undefined;
  }

  updateBeneficialOwner(model: BeneficialOwnerModel) {
    if (model.validate() && this.model) {
      this.model.updateBeneficialOwner(model.prepareData());
      this.closeEditDialog();
    }
  }

  navigateToOverview(replace?: boolean) {
    this.dispatch([navigate(adminBeneficialOwners(this.userStore.orgNumber), { replace })]);
  }

  findSelectedBeneficialOwner(beneficialOwnerId: string | undefined) {
    if (
      beneficialOwnerId &&
      isOk(this.fetchResult) &&
      this.fetchResult.data?.beneficialOwnersState.type === "knownBeneficialOwners"
    ) {
      return this.fetchResult.data?.beneficialOwnersState.beneficialOwners.find(
        (bo) => bo.id === beneficialOwnerId,
      );
    }
  }

  *openSearchDialog() {
    this.showSearchDialog = true;
    this.showMap = "beneficial-owners";
    yield this.searchForBeneficialOwners();
    yield this.fetchBeneficialOwnersMap();
    yield this.fetchOwnersMap();
  }

  *searchForBeneficialOwners() {
    this.searchResult = pending();
    try {
      const response: BeneficialOwnersSearchResponse =
        yield this.apiService.searchForBeneficialOwners(this.userStore.orgNumber);

      this.searchResult = ok(
        new OptionFieldModel<BeneficialOwner>({
          options: response.searchResults,
          idFn: (owner) => owner.id,
          labelFn: (owner) => ({ translated: owner.name ?? "" }),
          selectedIds: response.searchResults.map((owner) => owner.id),
        }),
      );
    } catch (e) {
      this.searchResult = failure();
    }
  }

  closeSearchDialog() {
    this.showSearchDialog = false;
    this.searchResult = undefined;
    this.showFullScreen = false;
  }

  get isSearchPending() {
    return (
      !isFailure(this.searchResult) && (isPending(this.searchResult) || isPending(this.mapResult))
    );
  }

  *downloadPdfMap() {
    yield this.apiService.downloadBeneficialOwnersPdfMap(this.userStore.orgNumber);
  }

  initializeNewFormFromSearchResults() {
    this.showFullScreen = false;
    this.showSearchDialog = false;
    this.dispatch([navigate(adminNewBeneficialOwnersStatement(this.userStore.orgNumber))]);
  }

  addBeneficialOwnersFromSearch() {
    this.showFullScreen = false;
    this.showSearchDialog = false;
    const model = this.model;
    if (model && isOk(this.searchResult)) {
      this.searchResult.data.selected.forEach((owner) => model.addBeneficialOwner(owner));
    }
  }

  toggleFullscreen() {
    this.showFullScreen = !this.showFullScreen;
  }

  setShowMap(map: "beneficial-owners" | "owners") {
    this.showMap = map;
  }
}

function makeAuthenticationUrl(authUrl: string, orgNumber: string) {
  const returnUrl = `${adminBeneficialOwners(orgNumber)}/submit`;
  return `${authUrl}?returnUrl=${returnUrl}`;
}

export type BeneficialOwnersResponse = {
  beneficialOwnersState: BeneficialOwnersState;
  status: BeneficialOwnersStatementStatus;
  submitInfo?: BeneficialOwnerSubmitInfo;
  statementLog: BeneficialOwnerLogItem[];
};

export type BeneficialOwnerSubmitInfo = {
  sentAt: string;
  sentBy: string;
};

export type BeneficialOwnersState =
  | KnownBeneficialOwners
  | NoBeneficialOwners
  | UnknownBeneficialOwners;

export type KnownBeneficialOwners = {
  type: "knownBeneficialOwners";
  beneficialOwners: BeneficialOwner[];
  hasOtherUnknownBeneficialOwners: boolean;
};

export type NoBeneficialOwners = {
  type: "noBeneficialOwners";
  controlledByPublicEntity: boolean;
  isPublicEntityForeign: boolean;
};

export type UnknownBeneficialOwners = {
  type: "unknownBeneficialOwners";
};

export type BeneficialOwnersStateType = BeneficialOwnersState["type"];

export type BeneficialOwnerLogItem = {
  id: string;
  sentAt?: string;
  sentBy?: string;
  beneficialOwnersState: BeneficialOwnersState;
};

export type BeneficialOwner = {
  id: string;
  name?: string;
  socialSecurityNumber?: string;
  foreignPersonData?: ForeignPersonData;
  shareholderId?: number;
  positionType: PositionType;
  ownershipPosition?: OwnershipPosition;
  votesPosition?: VotesPosition;
  otherDescription?: string;
};

export type ForeignPersonData = {
  name?: string;
  birthDate?: string;
  residenceCountry?: string;
  citizenCountries?: Array<string>;
};

export type OwnershipPosition = {
  interval: PositionInterval;
  basis: Array<OwnershipPositionBasis>;
  intermediateCompanies: Array<IntermediateCompany>;
};

export type VotesPosition = {
  interval: PositionInterval;
  basis: Array<VotingRightsPositionBasis>;
  intermediateCompanies: Array<IntermediateCompany>;
};

export type IntermediateCompany = {
  id: string;
  orgNo?: string;
  name?: string;
  foreignCompany?: ForeignCompanyData;
};

export type ForeignCompanyData = {
  orgNo?: string;
  name: string;
  country: string;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
};

export type PositionType = "OWNERSHIP" | "VOTING_RIGHTS" | "BOARD_MEMBERS" | "OTHER";

export type PositionInterval = "GT_25" | "GTE_50" | "GTE_75";

export type OwnershipPositionBasis = "DIRECT" | "INDIRECT";

export type VotingRightsPositionBasis = "DIRECT" | "INDIRECT" | "AGREEMENT";

export type BeneficialOwnersStatementStatus = "PENDING" | "SUBMITTED" | "ERROR";

export function formatPositionType(type: PositionType): TMessageKey {
  switch (type) {
    case "OWNERSHIP":
      return "beneficial-owners.position-type.ownership";
    case "VOTING_RIGHTS":
      return "beneficial-owners.position-type.voting-rights";
    case "BOARD_MEMBERS":
      return "beneficial-owners.position-type.boardMembers";
    case "OTHER":
      return "beneficial-owners.position-type.other";
  }
}

export function formatOwnershipPositionInterval(interval: PositionInterval): TMessageKey {
  switch (interval) {
    case "GT_25":
      return "beneficial-owners.ownership-over-25";
    case "GTE_50":
      return "beneficial-owners.ownership-50-or-over";
    case "GTE_75":
      return "beneficial-owners.ownership-75-or-over";
  }
}

export function formatVotingRightsPositionInterval(interval: PositionInterval): TMessageKey {
  switch (interval) {
    case "GT_25":
      return "beneficial-owners.voting-rights-over-25";
    case "GTE_50":
      return "beneficial-owners.voting-rights-50-or-over";
    case "GTE_75":
      return "beneficial-owners.voting-rights-75-or-over";
  }
}

export function formatOwnershipPositionBasis(basis: OwnershipPositionBasis): TMessageKey {
  switch (basis) {
    case "DIRECT":
      return "beneficial-owners.ownership-basis.direct";
    case "INDIRECT":
      return "beneficial-owners.ownership-basis.indirect";
  }
}

export function formatVotingRightsPositionBasis(basis: VotingRightsPositionBasis): TMessageKey {
  switch (basis) {
    case "DIRECT":
      return "beneficial-owners.voting-rights-basis.direct";
    case "INDIRECT":
      return "beneficial-owners.voting-rights-basis.indirect";
    case "AGREEMENT":
      return "beneficial-owners.voting-rights-basis.agreement";
  }
}

export function nameValidator(value: string): TMessage | undefined {
  if (value.match(/[^\p{L}\p{Z}\p{P}]/gu)) {
    return { key: "beneficial-owners.invalid-characters" };
  }
  const minLength = 2;
  if (value.length < minLength) {
    return { key: "minStringLength", params: { minLength } };
  }
}

export class BeneficialOwnerModel {
  beneficialOwnerId: string;
  name: StringFieldModel;
  hasSsn: RadioFieldModel<boolean>;
  birthDate: LocalDateFieldModel;
  socialSecurityNumber: StringFieldModel;
  residenceCountry: RadioFieldModel<{
    code: string;
    name: string;
  } | null>;
  citizenCountries: Array<
    RadioFieldModel<{
      code: string;
      name: string;
    } | null>
  >;

  positionType: RadioFieldModel<PositionType>;

  ownershipPositionInterval: RadioFieldModel<PositionInterval>;
  ownershipPositionBasis: OptionFieldModel<OwnershipPositionBasis>;
  votingRightsInterval: RadioFieldModel<PositionInterval>;
  votingRightsPositionBasis: OptionFieldModel<VotingRightsPositionBasis>;
  intermediateCompanies: IntermediateCompany[];
  intermediateCompanyModel?: IntermediateCompanyModel;

  error?: TMessage;
  otherDescription: StringFieldModel;

  constructor(
    readonly language: Language,
    readonly hasControlBoardMembers: boolean,
    readonly beneficialOwner?: BeneficialOwner,
  ) {
    this.beneficialOwnerId = beneficialOwner?.id ?? newUuid();
    this.name = new StringFieldModel({
      required: true,
      validator: nameValidator,
      value: beneficialOwner?.name,
    });
    this.hasSsn = new RadioFieldModel({
      value: beneficialOwner === undefined || beneficialOwner.socialSecurityNumber !== undefined,
      options: [true, false],
      idFn: (v) => String(v),
      labelFn: (value) => ({
        key: value ? "beneficial-owners.details.hasSsn" : "beneficial-owners.details.hasNotSsn",
      }),
    });
    this.socialSecurityNumber = new StringFieldModel({
      validator: norwegianPersonalIdValidator,
      required: true,
      value: beneficialOwner?.socialSecurityNumber,
    });

    const foreignPersonData = beneficialOwner?.foreignPersonData;
    this.birthDate = new LocalDateFieldModel({
      required: true,
      value: foreignPersonData?.birthDate,
    });
    this.residenceCountry = makeNullableCountryRadioFieldModel(
      language,
      foreignPersonData?.residenceCountry,
    );
    if (foreignPersonData?.citizenCountries && foreignPersonData.citizenCountries.length > 0) {
      this.citizenCountries = foreignPersonData.citizenCountries.map((c) =>
        makeNullableCountryRadioFieldModel(language, c),
      );
    } else {
      this.citizenCountries = [makeNullableCountryRadioFieldModel(language, undefined)];
    }

    this.positionType = new RadioFieldModel<PositionType>({
      options: ["OWNERSHIP", "VOTING_RIGHTS", "BOARD_MEMBERS", "OTHER"],
      value: beneficialOwner?.positionType ?? "OWNERSHIP",
      idFn: identity,
      labelFn: (value) => ({ key: formatPositionType(value) }),
    });
    this.ownershipPositionInterval = new RadioFieldModel<PositionInterval>({
      options: ["GT_25", "GTE_50", "GTE_75"],
      value: beneficialOwner?.ownershipPosition?.interval ?? "GT_25",
      idFn: (option) => String(option),
      labelFn: (option) => ({ key: formatOwnershipPositionInterval(option) }),
    });
    this.ownershipPositionBasis = new OptionFieldModel<OwnershipPositionBasis>({
      options: ["DIRECT", "INDIRECT"],
      idFn: (option) => String(option),
      labelFn: (option) => ({ key: formatOwnershipPositionBasis(option) }),
      required: true,
      value: beneficialOwner?.ownershipPosition?.basis,
    });

    this.votingRightsInterval = new RadioFieldModel<PositionInterval>({
      options: ["GT_25", "GTE_50", "GTE_75"],
      value: beneficialOwner?.votesPosition?.interval ?? "GT_25",
      idFn: (option) => String(option),
      labelFn: (option) =>
        option ? { key: formatVotingRightsPositionInterval(option) } : { translated: " " },
    });
    this.votingRightsPositionBasis = new OptionFieldModel<VotingRightsPositionBasis>({
      options: ["DIRECT", "INDIRECT", "AGREEMENT"],
      idFn: (option) => String(option),
      labelFn: (option) => ({ key: formatVotingRightsPositionBasis(option) }),
      required: true,
      value: beneficialOwner?.votesPosition?.basis,
    });

    const intermediateCompanies =
      beneficialOwner?.ownershipPosition?.intermediateCompanies ??
      beneficialOwner?.votesPosition?.intermediateCompanies ??
      [];
    this.intermediateCompanies = [...intermediateCompanies];

    this.otherDescription = new StringFieldModel({
      required: true,
      value: beneficialOwner?.otherDescription,
    });

    if (beneficialOwner) {
      this.validate();
    }

    makeAutoObservable(this, {}, { autoBind: true });
  }

  addCitizenCountry() {
    this.citizenCountries.push(makeNullableCountryRadioFieldModel(this.language, undefined));
  }

  prepareData(): BeneficialOwner {
    return {
      id: this.beneficialOwnerId,
      name: this.name.validatedValue,
      socialSecurityNumber: this.hasSsn.value
        ? this.socialSecurityNumber.validatedValue
        : undefined,
      foreignPersonData: this.hasSsn.value
        ? undefined
        : {
            name: this.name.validatedValue,
            birthDate: this.birthDate.toISOString(),
            residenceCountry: this.residenceCountry.value
              ? this.residenceCountry.value.code
              : undefined,
            citizenCountries: this.citizenCountries.flatMap((c) => (c.value ? [c.value.code] : [])),
          },
      positionType: this.positionType.value,
      ownershipPosition:
        this.positionType.value === "OWNERSHIP"
          ? {
              interval: this.ownershipPositionInterval.value,
              basis: this.ownershipPositionBasis.selected,
              intermediateCompanies: this.ownershipPositionBasis.isSelected("INDIRECT")
                ? this.intermediateCompanies
                : [],
            }
          : undefined,
      votesPosition:
        this.positionType.value === "VOTING_RIGHTS"
          ? {
              interval: this.votingRightsInterval.value,
              basis: this.votingRightsPositionBasis.selected,
              intermediateCompanies: this.votingRightsPositionBasis.isSelected("INDIRECT")
                ? this.intermediateCompanies
                : [],
            }
          : undefined,
      otherDescription:
        this.positionType.value === "OTHER" ? this.otherDescription.validatedValue : undefined,
    };
  }

  validate() {
    if (this.hasIndirectBasis) {
      if (this.intermediateCompanies.length === 0) {
        this.error = { key: "beneficial-owners.intermediate-companies.required" };
      } else if (isCompanyMissingAddress(this.intermediateCompanies)) {
        this.error = { key: "beneficial-owners.warning.intermediate-address" };
      } else {
        this.error = undefined;
      }
    } else if (this.hasControlBoardMembers) {
      this.error =
        this.positionType.value === "BOARD_MEMBERS"
          ? { key: "beneficial-owners.position-type.boardMembers.error" }
          : undefined;
    } else {
      this.error = undefined;
    }

    return (
      this.name.validate() &&
      (this.hasSsn.value
        ? this.socialSecurityNumber.validate()
        : this.birthDate.validate() &&
          this.residenceCountry.validate() &&
          this.citizenCountries.length > 0 &&
          this.citizenCountries[0].validate()) &&
      this.error === undefined &&
      ((this.positionType.value === "OWNERSHIP" && this.ownershipPositionBasis.validate()) ||
        (this.positionType.value === "VOTING_RIGHTS" &&
          this.votingRightsPositionBasis.validate()) ||
        this.positionType.value === "BOARD_MEMBERS" ||
        (this.positionType.value === "OTHER" && this.otherDescription.validate()))
    );
  }

  get hasIndirectBasis() {
    return (
      (this.positionType.value === "OWNERSHIP" &&
        this.ownershipPositionBasis.isSelected("INDIRECT")) ||
      (this.positionType.value === "VOTING_RIGHTS" &&
        this.votingRightsPositionBasis.isSelected("INDIRECT"))
    );
  }

  openAddNorwegianCompanyForm(language: Language) {
    this.intermediateCompanyModel = new IntermediateCompanyModel(
      this.addInterMediateCompany,
      () => (this.intermediateCompanyModel = undefined),
      language,
      true,
    );
  }

  openAddForeignCompanyDialog(language: Language) {
    this.intermediateCompanyModel = new IntermediateCompanyModel(
      this.addInterMediateCompany,
      () => (this.intermediateCompanyModel = undefined),
      language,
      false,
    );
  }

  addInterMediateCompany() {
    if (this.intermediateCompanyModel) {
      this.intermediateCompanies.push(this.intermediateCompanyModel.prepareData());
      this.intermediateCompanyModel = undefined;
      this.error = undefined;
    }
  }

  updateInterMediateCompany() {
    if (this.intermediateCompanyModel) {
      const index = this.intermediateCompanies.findIndex(
        (c) => c.id === this.intermediateCompanyModel?.intermediateCompany?.id,
      );
      if (index >= 0) {
        this.intermediateCompanies[index] = this.intermediateCompanyModel.prepareData();
        this.intermediateCompanyModel = undefined;
        this.error = undefined;
      }
    }
  }

  editIntermediateCompany(language: Language, company: IntermediateCompany) {
    this.intermediateCompanyModel = new IntermediateCompanyModel(
      this.updateInterMediateCompany,
      () => (this.intermediateCompanyModel = undefined),
      language,
      false,
      company,
    );
  }

  removeIntermediateCompany(id: string) {
    this.intermediateCompanies = this.intermediateCompanies.filter((c) => c.id !== id);
  }
}

export class IntermediateCompanyModel {
  searchQuery: string = "";
  company?: SuggestedCompany;
  error?: TMessage;
  orgNo: StringFieldModel;
  name: StringFieldModel;
  addressLine1: StringFieldModel;
  addressLine2: StringFieldModel;
  addressLine3: StringFieldModel;
  country: RadioFieldModel<{
    code: string;
    name: string;
  }>;

  constructor(
    readonly onSubmit: () => void,
    readonly onCancel: () => void,
    locale: Language,
    readonly isNorwegianCompany: boolean = true,
    readonly intermediateCompany?: IntermediateCompany,
  ) {
    this.orgNo = new StringFieldModel({
      value: intermediateCompany?.orgNo ?? intermediateCompany?.foreignCompany?.orgNo,
      required: true,
      validator: minLengthValidator(3),
    });
    this.name = new StringFieldModel({
      value: intermediateCompany?.name ?? intermediateCompany?.foreignCompany?.name,
      required: true,
      validator: minLengthValidator(3),
    });
    this.addressLine1 = new StringFieldModel({
      value: intermediateCompany?.foreignCompany?.addressLine1,
      required: true,
      validator: minLengthValidator(3),
    });
    this.addressLine2 = new StringFieldModel({
      value: intermediateCompany?.foreignCompany?.addressLine2,
    });
    this.addressLine3 = new StringFieldModel({
      value: intermediateCompany?.foreignCompany?.addressLine3,
    });
    this.country = makeCountryRadioFieldModel(locale, intermediateCompany?.foreignCompany?.country);

    if (intermediateCompany) {
      this.validate();
    }

    makeAutoObservable(this, { onSubmit: false, onCancel: false }, { autoBind: true });
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
  }

  selectCompany(company?: SuggestedCompany) {
    this.company = company;
  }

  validate() {
    if (this.isNorwegianCompany) {
      if (this.company === undefined) {
        this.error = { key: "required" };
      } else {
        this.error = undefined;
      }
      return this.error === undefined;
    } else {
      return this.name.validate() && this.addressLine1.validate() && this.country.validate();
    }
  }

  submit() {
    if (this.validate()) {
      this.onSubmit();
    }
  }

  cancel() {
    this.setSearchQuery("");
    this.onCancel();
  }

  prepareData(): IntermediateCompany {
    return {
      id: newUuid(),
      orgNo: this.isNorwegianCompany ? this.company?.organizationnumber : undefined,
      name: this.isNorwegianCompany ? this.company?.name : undefined,
      foreignCompany: this.isNorwegianCompany
        ? undefined
        : {
            orgNo: this.orgNo.validatedValue,
            name: this.name.validatedValue,
            country: this.country.value.code,
            addressLine1: this.addressLine1.validatedValue,
            addressLine2: this.addressLine2.validatedValue
              ? this.addressLine2.validatedValue
              : undefined,
            addressLine3: this.addressLine3.validatedValue
              ? this.addressLine3.validatedValue
              : undefined,
          },
    };
  }
}

function makeCountryRadioFieldModel(locale: Language, countryCode: string | undefined) {
  const countries = getAllCountriesSortedByName(locale);
  return new RadioFieldModel<{
    code: string;
    name: string;
  }>({
    options: countries,
    value: countries.find((c) => c.code === countryCode) ?? countries[0],
    idFn: (item) => item.code,
    labelFn: (item) => ({ translated: item.name }),
    required: true,
  });
}

function makeNullableCountryRadioFieldModel(locale: Language, countryCode: string | undefined) {
  const countries = getAllCountriesSortedByName(locale);
  return new RadioFieldModel<{
    code: string;
    name: string;
  } | null>({
    options: [null, ...countries],
    value: countries.find((c) => c.code === countryCode) ?? null,
    idFn: (item) => (item ? item.code : ""),
    labelFn: (item) => ({ translated: item ? item.name : " " }),
    required: true,
  });
}

export function formatPosition(beneficialOwner: BeneficialOwner): TMessage | undefined {
  if (beneficialOwner.ownershipPosition) {
    return { key: formatOwnershipPositionInterval(beneficialOwner.ownershipPosition.interval) };
  } else if (beneficialOwner.votesPosition) {
    return { key: formatVotingRightsPositionInterval(beneficialOwner.votesPosition.interval) };
  } else if (beneficialOwner.positionType === "BOARD_MEMBERS") {
    return { key: "beneficial-owners.position-type.boardMembers" };
  } else if (beneficialOwner.positionType === "OTHER") {
    return { key: "beneficial-owners.position-type.other" };
  }
}

export class BeneficialOwnersModel {
  beneficialOwnersStateType: RadioFieldModel<BeneficialOwnersStateType | undefined>;
  hasOtherUnknownBeneficialOwners: RadioFieldModel<boolean>;
  controlledByPublicEntity: RadioFieldModel<boolean>;
  isPublicEntityForeign: RadioFieldModel<boolean>;
  beneficialOwners: BeneficialOwner[];
  isDirty: boolean = false;
  error?: TMessage;

  constructor(readonly state?: BeneficialOwnersState) {
    this.beneficialOwnersStateType = new RadioFieldModel({
      value: state?.type,
      options: ["knownBeneficialOwners", "noBeneficialOwners", "unknownBeneficialOwners"],
      idFn: identity,
      labelFn: (value) =>
        value ? { key: formatBeneficialOwnersStateType(value) } : { translated: "" },
      onChange: () => this.setDirty(),
    });
    this.hasOtherUnknownBeneficialOwners = new RadioFieldModel({
      value:
        state?.type === "knownBeneficialOwners" ? state.hasOtherUnknownBeneficialOwners : false,
      options: [true, false],
      idFn: String,
      labelFn: (value) => ({ key: value ? "yes" : "no" }),
      onChange: () => this.setDirty(),
    });
    this.controlledByPublicEntity = new RadioFieldModel({
      value: state?.type === "noBeneficialOwners" ? state.controlledByPublicEntity : false,
      options: [true, false],
      idFn: String,
      labelFn: (value) => ({ key: value ? "yes" : "no" }),
      onChange: () => this.setDirty(),
    });
    this.isPublicEntityForeign = new RadioFieldModel({
      value: state?.type === "noBeneficialOwners" ? state.isPublicEntityForeign : false,
      options: [true, false],
      idFn: String,
      labelFn: (value) => ({ key: value ? "yes" : "no" }),
      onChange: () => this.setDirty(),
    });
    this.beneficialOwners =
      state?.type === "knownBeneficialOwners" ? [...state.beneficialOwners] : [];

    makeAutoObservable(this, {}, { autoBind: true });
  }

  prepareData(): BeneficialOwnersState | undefined {
    switch (this.beneficialOwnersStateType.value) {
      case "unknownBeneficialOwners":
        return { type: "unknownBeneficialOwners" };
      case "knownBeneficialOwners":
        return {
          type: "knownBeneficialOwners",
          beneficialOwners: this.beneficialOwners,
          hasOtherUnknownBeneficialOwners: this.hasOtherUnknownBeneficialOwners.value,
        };
      case "noBeneficialOwners":
        return {
          type: "noBeneficialOwners",
          controlledByPublicEntity: this.controlledByPublicEntity.value,
          isPublicEntityForeign: this.isPublicEntityForeign.value,
        };
    }
  }

  updateBeneficialOwner(beneficialOwner: BeneficialOwner) {
    this.setDirty();
    this.beneficialOwners = this.beneficialOwners.map((owner) =>
      owner.id === beneficialOwner.id ? beneficialOwner : owner,
    );
  }

  hasControlBoardMembers(excludedOwner?: BeneficialOwner) {
    return this.beneficialOwners.some(
      (owner) => owner !== excludedOwner && owner.positionType === "BOARD_MEMBERS",
    );
  }

  validate() {
    this.error = undefined;

    if (this.beneficialOwnersStateType.value === "knownBeneficialOwners") {
      if (this.beneficialOwners.length === 0) {
        this.error = { key: "beneficial-owners.edit.no-beneficial-owners" };
      } else {
        const ssns = compact(this.beneficialOwners.map((owner) => owner.socialSecurityNumber));
        const countBySsn = countBy(this.beneficialOwners, (owner) => owner.socialSecurityNumber);
        if (ssns.some((ssn) => countBySsn[ssn] > 1)) {
          this.error = { key: "beneficial-owners.duplicate-ssn" };
        }
      }
    }
  }

  get isValid() {
    if (this.beneficialOwnersStateType.value === "knownBeneficialOwners") {
      if (this.beneficialOwners.length === 0) return false;
    }
    return this.error === undefined;
  }

  addBeneficialOwner(beneficialOwner: BeneficialOwner) {
    this.setDirty();
    this.beneficialOwners.push(beneficialOwner);
  }

  setDirty() {
    this.isDirty = true;
  }
}

export class BeneficialOwnersApiClient {
  private http: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.http = axiosInstance;
  }

  async fetchBeneficialOwners(orgNumber: string) {
    const res = await this.http.get(`/beneficialOwners/${orgNumber}`);
    return await res.data;
  }

  async getBeneficialOwnerSessionState(orgNumber: string): Promise<BeneficialOwnersState | null> {
    const res = await this.http.get(`/beneficialOwners/${orgNumber}/session`);
    return await res.data;
  }

  async fetchBeneficialOwnersStatement(orgNumber: string, statementId: string) {
    const res = await this.http.get(`/beneficialOwners/${orgNumber}/${statementId}`);
    return await res.data;
  }

  async searchForBeneficialOwners(orgNumber: string): Promise<BeneficialOwnersSearchResponse> {
    const res = await this.http.get(`/beneficialOwners/${orgNumber}/search`);
    return await res.data;
  }

  async submitBeneficialOwners(orgNumber: string, state?: BeneficialOwnersState) {
    const res = await this.http.post(`/beneficialOwners/${orgNumber}/submit`, state);
    return await res.data;
  }

  async fetchBeneficialOwnersMap(orgNumber: string): Promise<string> {
    const r = await this.http.get(`/beneficialOwners/${orgNumber}/map`);
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(r.data)))}`;
  }

  async downloadBeneficialOwnersPdfMap(orgNumber: string): Promise<void> {
    const response = await this.http.get(`/beneficialOwners/${orgNumber}/pdfMap`, {
      responseType: "blob",
    });
    downloadFile(response.data, getResponseFilename(response));
  }
}

export function formatBeneficialOwnersStateType(type: BeneficialOwnersStateType): TMessageKey {
  switch (type) {
    case "knownBeneficialOwners":
      return "beneficial-owners.type.known-beneficial-owners";
    case "noBeneficialOwners":
      return "beneficial-owners.type.no-beneficial-owners";
    case "unknownBeneficialOwners":
      return "beneficial-owners.type.unknown-beneficial-owners";
  }
}

type SubmitBeneficialOwnersResponse =
  | { type: "AuthenticationRequired"; authUrl: string }
  | { type: "ValidationErrors"; errors: string[] }
  | { type: "NoAccessError" }
  | { type: "NotAuthorizedError" };

export const beneficialOwnersDocsUrl = "https://dcompany.no/docs/reelle-rettighetshavere/";

export function openBeneficialOwnersDocumentation() {
  window.open(beneficialOwnersDocsUrl);
}
